import { groupBy } from 'lodash';
import { REQUEST_TYPES } from '../requestTypes';
import { ENTITY_NAMES } from '../entityNames';
import { ENDPOINTS } from '../../../config/config';
import { sentimentStatus } from '../../../utils/callHistory';
import { DEFAULT_PAGE_SIZE, GRANULARITY, GROUP_BY, SORT_BY, SORT_ORDER } from '../../../utils/constants';
import { type RequestHandlerFactory } from '../../types';
import { DASHBOARD_API_URLS } from '../../constants';

interface SentimentData {
    negative: number;
    neutral: number;
    positive: number;
}

interface SentimentWithStatus extends SentimentData {
    status: string;
    ratioMultiplier: number;
}

interface TimeSeriesEntry {
    date: string;
    agentNegative: number | null;
    agentNeutral: number | null;
    agentPositive: number | null;
    customerNegative: number | null;
    customerNeutral: number | null;
    customerPositive: number | null;
    numberOfCalls?: number;
}

interface RawSentimentEntry {
    date: string;
    id: number;
    name: string;
    agentNegative: number | null;
    agentNeutral: number | null;
    agentPositive: number | null;
    customerNegative: number | null;
    customerNeutral: number | null;
    customerPositive: number | null;
}

interface ProcessedSentimentEntry {
    date: string;
    id: number;
    name: string;
    agentSentiment: SentimentData;
    customerSentiment: SentimentData;
}

interface TeamAverageSentiment {
    date: string;
    id: number | string;
    name: string;
    agentSentiment: SentimentWithStatus;
    customerSentiment: SentimentWithStatus;
}

type TrendEntry = Record<string, any>;

const getLocationId = (): number => {
    const userJson = localStorage.getItem('persist:user');
    if (!userJson) return 0;
    const user = JSON.parse(userJson) as { locationId: string };
    return Number(user.locationId);
};

const hasSentimentData = ({ negative, neutral, positive }: SentimentData): boolean => {
    return (
        (negative > 0 && negative < Infinity) ||
        (neutral > 0 && neutral < Infinity) ||
        (positive > 0 && positive < Infinity)
    );
};

function aggregateTimeSeries(timeSeriesArray: TimeSeriesEntry[]) {
    const aggregatedObject = {
        agentNegative: 0,
        agentNeutral: 0,
        agentPositive: 0,
        customerNegative: 0,
        customerNeutral: 0,
        customerPositive: 0,
        numberOfCalls: 0,
        agentCount: 0,
        customerCount: 0
    };

    timeSeriesArray.forEach(obj => {
        aggregatedObject.agentNegative += obj.agentNegative ?? 0;
        aggregatedObject.agentNeutral += obj.agentNeutral ?? 0;
        aggregatedObject.agentPositive += obj.agentPositive ?? 0;
        aggregatedObject.customerNegative += obj.customerNegative ?? 0;
        aggregatedObject.customerNeutral += obj.customerNeutral ?? 0;
        aggregatedObject.customerPositive += obj.customerPositive ?? 0;
        aggregatedObject.numberOfCalls += obj.numberOfCalls ?? 0;
        if (obj.agentNegative !== null && obj.agentNeutral !== null && obj.agentPositive !== null) {
            aggregatedObject.agentCount++;
        }
        if (obj.customerNegative !== null && obj.customerNeutral !== null && obj.customerPositive !== null) {
            aggregatedObject.customerCount++;
        }
    });

    if (aggregatedObject.agentCount > 0) {
        aggregatedObject.agentNegative /= aggregatedObject.agentCount;
        aggregatedObject.agentNeutral /= aggregatedObject.agentCount;
        aggregatedObject.agentPositive /= aggregatedObject.agentCount;
    }
    if (aggregatedObject.customerCount > 0) {
        aggregatedObject.customerNegative /= aggregatedObject.customerCount;
        aggregatedObject.customerNeutral /= aggregatedObject.customerCount;
        aggregatedObject.customerPositive /= aggregatedObject.customerCount;
    }

    return aggregatedObject;
}

function withAverageForTeam(
    teams: Record<string, ProcessedSentimentEntry[]>,
    calculateAveragePercentages: (teamData: ProcessedSentimentEntry[]) => TeamAverageSentiment,
    formatNumber: (num: number) => number,
    locationData: TimeSeriesEntry[]
): TeamAverageSentiment[] {
    let output = Object.values(teams).map(calculateAveragePercentages);

    // reduce to average for all teams over time period
    const allTeamAverage = aggregateTimeSeries(locationData);

    output = [
        {
            date: '',
            id: 'average',
            name: 'All Teams Average',
            agentSentiment: {
                negative: allTeamAverage.agentNegative,
                neutral: allTeamAverage.agentNeutral,
                positive: allTeamAverage.agentPositive,
                status: hasSentimentData({
                    negative: allTeamAverage.agentNegative,
                    neutral: allTeamAverage.agentNeutral,
                    positive: allTeamAverage.agentPositive
                })
                    ? sentimentStatus.AVAILABLE
                    : sentimentStatus.NOT_AVAILABLE,
                ratioMultiplier: 160
            },
            customerSentiment: {
                negative: allTeamAverage.customerNegative,
                neutral: allTeamAverage.customerNeutral,
                positive: allTeamAverage.customerPositive,
                status: hasSentimentData({
                    negative: allTeamAverage.customerNegative,
                    neutral: allTeamAverage.customerNeutral,
                    positive: allTeamAverage.customerPositive
                })
                    ? sentimentStatus.AVAILABLE
                    : sentimentStatus.NOT_AVAILABLE,
                ratioMultiplier: 160
            }
        },
        ...output
    ];

    return output;
}

const transformResponse = (
    data: RawSentimentEntry[] = [],
    locationData: TimeSeriesEntry[] = []
): TeamAverageSentiment[] => {
    const percentagesData: ProcessedSentimentEntry[] = data.map(entry => ({
        ...entry,
        agentSentiment: {
            negative: (entry.agentNegative ?? 0) * 100,
            neutral: (entry.agentNeutral ?? 0) * 100,
            positive: (entry.agentPositive ?? 0) * 100
        },
        customerSentiment: {
            negative: (entry.customerNegative ?? 0) * 100,
            neutral: (entry.customerNeutral ?? 0) * 100,
            positive: (entry.customerPositive ?? 0) * 100
        }
    }));

    function formatNumber(number: number): number {
        if (!number || number === Infinity) {
            return 0;
        }
        return Math.round(number + Number.EPSILON) / 100;
    }

    // Function to calculate average percentages for a team
    const calculateAveragePercentages = (teamData: ProcessedSentimentEntry[]): TeamAverageSentiment => {
        let agentCount = 0;
        let customerCount = 0;

        const totalAgentSentiment: SentimentData = { negative: 0, neutral: 0, positive: 0 };
        const totalCustomerSentiment: SentimentData = { negative: 0, neutral: 0, positive: 0 };

        teamData.forEach(entry => {
            if (hasSentimentData(entry.agentSentiment)) {
                agentCount++;
            }
            if (hasSentimentData(entry.customerSentiment)) {
                customerCount++;
            }
            totalAgentSentiment.negative += entry.agentSentiment.negative;
            totalAgentSentiment.neutral += entry.agentSentiment.neutral;
            totalAgentSentiment.positive += entry.agentSentiment.positive;
            totalCustomerSentiment.negative += entry.customerSentiment.negative;
            totalCustomerSentiment.neutral += entry.customerSentiment.neutral;
            totalCustomerSentiment.positive += entry.customerSentiment.positive;
        });

        return {
            date: teamData[0].date,
            id: teamData[0].id,
            name: teamData[0].name,
            agentSentiment: {
                negative: formatNumber(totalAgentSentiment.negative / agentCount),
                neutral: formatNumber(totalAgentSentiment.neutral / agentCount),
                positive: formatNumber(totalAgentSentiment.positive / agentCount),
                status: hasSentimentData(totalAgentSentiment)
                    ? sentimentStatus.AVAILABLE
                    : sentimentStatus.NOT_AVAILABLE,
                ratioMultiplier: 160
            },
            customerSentiment: {
                negative: formatNumber(totalCustomerSentiment.negative / customerCount),
                neutral: formatNumber(totalCustomerSentiment.neutral / customerCount),
                positive: formatNumber(totalCustomerSentiment.positive / customerCount),
                status: hasSentimentData(totalCustomerSentiment)
                    ? sentimentStatus.AVAILABLE
                    : sentimentStatus.NOT_AVAILABLE,
                ratioMultiplier: 160
            }
        };
    };

    // Group entries by team
    const teams = groupBy(percentagesData, 'id');

    const output = withAverageForTeam(teams, calculateAveragePercentages, formatNumber, locationData);

    return output;
};

function transformToTrend(
    report: TrendEntry[],
    locationReport: TimeSeriesEntry[],
    type: 'customer' | 'agent' = 'customer'
): TrendEntry[] {
    const locationByDate = new Map(locationReport.map(item => [item.date, item]));
    return report.map(item => {
        const location = locationByDate.get(item.date);
        return {
            ...item,
            name: String(item.id),
            allTeamAveragePositive: Number(location?.[`${type}Positive` as keyof TimeSeriesEntry] ?? 0).toFixed(2),
            allTeamAverageNegative: Number(location?.[`${type}Negative` as keyof TimeSeriesEntry] ?? 0).toFixed(2)
        };
    });
}

export const sentimentAnalysisRequests: Record<string, RequestHandlerFactory> = {
    [REQUEST_TYPES.GET_SENTIMENT_ANALYSIS]: params => {
        return {
            transformResponse: res => {
                const data = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_TEAM_COMPARISON]?.data;
                const locationData = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]?.data;
                const items = transformResponse(data.report, locationData.report);
                return {
                    entities: {
                        [ENTITY_NAMES.SENTIMENT_ANALYSIS_TEAM_COMPARISON]: {
                            items,
                            itemsMeta: {
                                total: items.length,
                                limit: DEFAULT_PAGE_SIZE
                            }
                        }
                    }
                };
            },
            descriptors: {
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_TEAM_COMPARISON]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.TEAM,
                        filter: params.filter,
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                },
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.LOCATION,
                        filter: [getLocationId()],
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                }
            }
        };
    },
    [REQUEST_TYPES.GET_SENTIMENT_TRENDS_CUSTOMER]: params => {
        return {
            transformResponse: res => {
                const data = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_CUSTOMER]?.data;
                const locationData = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]?.data;
                const items = transformToTrend(data.report, locationData.report);

                return {
                    entities: {
                        [ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_CUSTOMER]: {
                            items,
                            itemsMeta: {
                                total: 1,
                                limit: DEFAULT_PAGE_SIZE
                            }
                        }
                    }
                };
            },
            descriptors: {
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_CUSTOMER]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.TEAM,
                        filter: params.filter,
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                },
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.LOCATION,
                        filter: [getLocationId()],
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                }
            }
        };
    },
    [REQUEST_TYPES.GET_SENTIMENT_TRENDS_AGENT]: params => {
        return {
            transformResponse: res => {
                const data = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_AGENT]?.data;
                const locationData = res[ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]?.data;
                const items = transformToTrend(data.report, locationData.report, 'agent');

                return {
                    entities: {
                        [ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_AGENT]: {
                            items,
                            itemsMeta: {
                                total: 1,
                                limit: DEFAULT_PAGE_SIZE
                            }
                        }
                    }
                };
            },
            descriptors: {
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_TREND_AGENT]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.TEAM,
                        filter: params.filter,
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                },
                [ENTITY_NAMES.SENTIMENT_ANALYSIS_LOCATION_COMPARISON]: {
                    method: 'post',
                    defaultEndpoint: ENDPOINTS.BASE_URL,
                    url: DASHBOARD_API_URLS.V2.SENTIMENT_REPORT,
                    data: {
                        dates: params.dates,
                        filterBy: GROUP_BY.LOCATION,
                        filter: [getLocationId()],
                        granularity: GRANULARITY.DAY,
                        sortBy: SORT_BY.DATE,
                        sortOrder: SORT_ORDER.ASC
                    }
                }
            }
        };
    }
};
